import { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import Button from '../../components/ui/Button';
import { getPartCategoryLabel, getPartUnitLabel } from '../../constants/partCategories';
import { IconX, IconSearch, IconAlertCircle } from '@tabler/icons-react';

export default function AddPartToIntervalModal({ makeId, modelId, interval, onClose }) {
  const [allParts, setAllParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPart, setSelectedPart] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'parts'), snap => {
      setAllParts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const existingPartIds = (interval?.partsRequired || []).map(p => p.partId);

  const filteredParts = useMemo(() => {
    if (!search) return allParts.slice(0, 20);
    const s = search.toLowerCase();
    return allParts.filter(p =>
      p.partNumber?.toLowerCase().includes(s) ||
      p.name?.toLowerCase().includes(s) ||
      p.manufacturer?.toLowerCase().includes(s)
    ).slice(0, 50);
  }, [search, allParts]);

  const selectPart = (part) => {
    if (existingPartIds.includes(part.id)) {
      const existing = interval.partsRequired.find(p => p.partId === part.id);
      setDuplicateInfo({ part, existingQuantity: existing.quantity });
      setSelectedPart(part);
      setQuantity(String(existing.quantity));
      setNotes(existing.notes || '');
    } else {
      setDuplicateInfo(null);
      setSelectedPart(part);
      setQuantity('1');
      setNotes('');
    }
  };

  const handleAdd = async () => {
    if (!selectedPart) { setError('Pick a part first.'); return; }
    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) { setError('Quantity must be positive.'); return; }
    setError(''); setSaving(true);

    try {
      const newPart = {
        partId: selectedPart.id,
        partNumber: selectedPart.partNumber,
        partName: selectedPart.name,
        partCategory: selectedPart.category,
        unit: selectedPart.unit,
        quantity: qty,
        notes: notes.trim() || null,
      };

      let newPartsRequired;
      if (duplicateInfo) {
        newPartsRequired = (interval.partsRequired || []).map(p =>
          p.partId === selectedPart.id ? newPart : p
        );
      } else {
        newPartsRequired = [...(interval.partsRequired || []), newPart];
      }

      await updateDoc(
        doc(db, 'makes', makeId, 'models', modelId, 'serviceIntervals', interval.id),
        { partsRequired: newPartsRequired, updatedAt: serverTimestamp() }
      );
      onClose();
    } catch (err) {
      console.error(err);
      setError('Failed: ' + err.message);
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ width: 580, maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--color-bg-primary)', borderRadius: 14, boxShadow: 'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '0.5px solid var(--color-border-light)' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>Add part to interval</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{interval?.name}</div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}><IconX size={18} stroke={1.5} /></div>
        </div>

        <div style={{ padding: 20, borderBottom: '0.5px solid var(--color-border-light)' }}>
          <div style={{ position: 'relative' }}>
            <IconSearch size={14} stroke={1.5} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search part number, name, manufacturer..."
              autoFocus
              style={{ paddingLeft: 32 }}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', minHeight: 200, maxHeight: 280 }}>
          {loading ? (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading parts...</div>
          ) : filteredParts.length === 0 ? (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {search ? 'No matching parts. Add the part to the library first.' : 'No parts in library yet.'}
            </div>
          ) : (
            filteredParts.map(p => {
              const isExisting = existingPartIds.includes(p.id);
              const isSelected = selectedPart?.id === p.id;
              return (
                <div
                  key={p.id}
                  onClick={() => selectPart(p)}
                  style={{
                    padding: '10px 20px',
                    cursor: 'pointer',
                    background: isSelected ? 'var(--color-brand-light)' : 'transparent',
                    borderBottom: '0.5px solid var(--color-border-light)',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-secondary)', minWidth: 100 }}>{p.partNumber}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13 }}>{p.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', display: 'flex', gap: 8 }}>
                      <span>{getPartCategoryLabel(p.category)}</span>
                      {p.manufacturer && <span>· {p.manufacturer}</span>}
                      <span>· {getPartUnitLabel(p.unit)}</span>
                    </div>
                  </div>
                  {isExisting && (
                    <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 'var(--radius-full)', background: 'var(--color-warning-light)', color: 'var(--color-warning)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Already added
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>

        {selectedPart && (
          <div style={{ padding: 20, borderTop: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
            {duplicateInfo && (
              <div style={{ padding: '8px 12px', background: 'var(--color-warning-light)', borderRadius: 'var(--radius-md)', fontSize: 12, color: 'var(--color-warning)', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <IconAlertCircle size={14} stroke={1.5} />
                This part is already on this interval (quantity: {duplicateInfo.existingQuantity}). Save will overwrite the existing entry.
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 2 }}>{selectedPart.name}</div>
                <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', fontFamily: 'var(--font-mono)' }}>{selectedPart.partNumber}</div>
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 2 }}>Quantity</label>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input type="number" step="0.1" value={quantity} onChange={e => setQuantity(e.target.value)} style={{ width: 70 }} />
                  <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{getPartUnitLabel(selectedPart.unit)}</span>
                </div>
              </div>
            </div>

            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" style={{ fontSize: 12 }} />

            {error && <div style={{ padding: '6px 10px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 11, marginTop: 8 }}>{error}</div>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 20px', borderTop: '0.5px solid var(--color-border-light)' }}>
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button onClick={handleAdd} loading={saving} disabled={!selectedPart}>
            {duplicateInfo ? 'Update part on interval' : 'Add to interval'}
          </Button>
        </div>
      </div>
    </div>
  );
}
